export interface Provider {
  id: string;
  capability: string;
  status: "not_probed" | "healthy" | "unhealthy" | "disabled";
  lastProbedAt?: string;
  latencyMs?: number;
  tools?: string[];
  error?: string;
}

export interface ConfigurationProvider {
  id: string;
  capability: string;
  enabled: boolean;
  transport: string;
  endpoint?: string;
  command?: string;
  secretConfigured: boolean;
  tools: Record<string, string>;
  timeoutMs: number;
  fallbackBindingIds: string[];
}

export interface PortalConfiguration {
  version: string;
  environment: string;
  providers: ConfigurationProvider[];
}

export interface AnalysisRun {
  id: string;
  type: "position_analysis" | "portfolio_analysis" | "top_five" | "intraday_suggestions" | "mutual_fund_analysis" | "mutual_fund_suggestions";
  status: string;
  request: Record<string, unknown>;
  createdAt: string;
  stages: Array<{ name: string; status: string; detail?: string }>;
  result?: {
    outcome: string;
    confidence: string;
    rationale: string;
    warnings: string[];
    providerEvidence: Array<{ capability: string; providerId?: string; status: string; detail: string }>;
    recommendations?: Array<{
      rank: number;
      symbol: string;
      company: string;
      exchange: "NSE" | "BSE";
      score: string;
      confidence: string;
      rationale: string;
      catalysts: string[];
      risks: string[];
    }>;
    intradayRecommendations?: Array<{
      rank: number;
      symbol: string;
      company: string;
      exchange: "NSE" | "BSE";
      confidence: string;
      entryLow: string;
      entryHigh: string;
      targetPrice: string;
      stopLoss: string;
      riskRewardRatio: string;
      validFor: string;
      rationale: string;
      signals: string[];
      catalysts: string[];
      risks: string[];
    }>;
    fundRecommendations?: Array<{
      rank: number;
      schemeCode: string;
      fundName: string;
      category: string;
      amc: string;
      score: string;
      confidence: string;
      rationale: string;
      strengths: string[];
      risks: string[];
    }>;
    portfolioAssessments?: Array<{
      symbol: string;
      companyName: string;
      isin: string;
      quantity: string;
      averageCostPrice: string;
      currentMarketPrice: string;
      outcome: "buy" | "sell" | "hold" | "insufficient_evidence";
      confidence: string;
      rationale: string;
      suggestion: string;
      risks: string[];
      discussion: NonNullable<AnalysisRun["result"]>["discussion"];
    }>;
    discussion?: Array<{
      agent: "technical" | "fundamental" | "predictive" | "institutional" | "performance" | "risk" | "portfolio" | "cost" | "moderator";
      status: "succeeded" | "unavailable" | "failed";
      stance: "positive" | "negative" | "neutral" | "mixed" | "unavailable";
      conclusion: string;
      score?: string;
      confidence?: string;
      evidence: string[];
      risks: string[];
    }>;
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", ...init });
  const body = (await response.json()) as T & { title?: string };
  if (!response.ok) throw new Error(body.title ?? `Request failed (${response.status})`);
  return body;
}

export const api = {
  session: () => request<{ authenticated: boolean; username?: "admin" }>("/api/v1/auth/session"),
  login: (password: string) => request<{ authenticated: true; username: "admin" }>("/api/v1/auth/login", jsonBody({ username: "admin", password })),
  logout: () => request<{ authenticated: false }>("/api/v1/auth/logout", { method: "POST" }),
  configuration: () => request<PortalConfiguration>("/api/v1/configuration"),
  providers: () => request<{ providers: Provider[] }>("/api/v1/providers"),
  probe: (id: string) => request<Provider>(`/api/v1/providers/${id}/probe`, { method: "POST" }),
  runs: () => request<{ runs: AnalysisRun[] }>("/api/v1/analysis-runs"),
  deleteRun: (id: string) => request<{ deleted: true }>(`/api/v1/analysis-runs/${encodeURIComponent(id)}`, { method: "DELETE" }),
  position: (payload: unknown) => request<AnalysisRun>("/api/v1/analysis-runs", jsonCommand(payload)),
  portfolio: (payload: unknown) => request<AnalysisRun>("/api/v1/portfolio-analysis-runs", jsonCommand(payload)),
  recommendations: (payload: unknown) => request<AnalysisRun>("/api/v1/recommendation-runs", jsonCommand(payload)),
  intradaySuggestions: (payload: unknown) => request<AnalysisRun>("/api/v1/intraday-suggestion-runs", jsonCommand(payload)),
  mutualFund: (payload: unknown) => request<AnalysisRun>("/api/v1/mutual-fund-analysis-runs", jsonCommand(payload)),
  fundSuggestions: (payload: unknown) => request<AnalysisRun>("/api/v1/mutual-fund-suggestion-runs", jsonCommand(payload)),
};

function jsonCommand(payload: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify(payload),
  };
}

function jsonBody(payload: unknown): RequestInit {
  return { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) };
}
